import axios from "axios";

export const SUPPORT_GRANT_KEY = "support_grant";
export const SUPPORT_GRANT_EXPIRES_KEY = "support_grant_expires_at";

export const getSupportGrant = () => localStorage.getItem(SUPPORT_GRANT_KEY);

export const setSupportGrant = (token, expiresAt) => {
  localStorage.setItem(SUPPORT_GRANT_KEY, token);
  if (expiresAt) {
    localStorage.setItem(SUPPORT_GRANT_EXPIRES_KEY, expiresAt);
  }
};

export const clearSupportGrant = () => {
  localStorage.removeItem(SUPPORT_GRANT_KEY);
  localStorage.removeItem(SUPPORT_GRANT_EXPIRES_KEY);
};

export const isInSupportMode = () => Boolean(getSupportGrant());

// Decode a base64url-encoded JWT segment into an object (null on any failure).
const decodeJwtSegment = (segment) => {
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
};

// Expiry from the grant's JWT `exp` claim, in epoch ms (null if not a JWT / no exp).
export const decodeSupportGrantExpiryMs = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;
  const payload = decodeJwtSegment(parts[1]);
  if (!payload || !payload.exp) return null;
  return payload.exp * 1000;
};

// Single source of truth for the grant expiry, in epoch ms (null if unknown).
// Prefers a stored ISO timestamp, then falls back to the token's JWT `exp`.
export const getSupportGrantExpiryMs = () => {
  const stored = localStorage.getItem(SUPPORT_GRANT_EXPIRES_KEY);
  if (stored) {
    const ms = Date.parse(stored.endsWith("Z") ? stored : stored + "Z");
    if (Number.isFinite(ms)) return ms;
  }
  return decodeSupportGrantExpiryMs(getSupportGrant());
};

export const goToSupportExpired = () => {
  clearSupportGrant();
  if (window.location.pathname !== "/support-expired") {
    window.location.href = "/support-expired";
  }
};

let interceptorInstalled = false;
let expiryTimerId = null;

const isUserDataRequest = (config) => {
  const url = config?.url || "";
  const method = (config?.method || "get").toLowerCase();
  return method === "get" && /\/v1\/user$/.test(url.split("?")[0]);
};

let userDataInFlight = false;

const scheduleExpiryWatcher = () => {
  if (expiryTimerId) {
    clearInterval(expiryTimerId);
    expiryTimerId = null;
  }
  if (!Number.isFinite(getSupportGrantExpiryMs())) return;

  const check = () => {
    if (!isInSupportMode()) {
      clearInterval(expiryTimerId);
      expiryTimerId = null;
      return;
    }
    const expMs = getSupportGrantExpiryMs();
    if (Number.isFinite(expMs) && expMs <= Date.now()) {
      goToSupportExpired();
    }
  };
  check();
  expiryTimerId = setInterval(check, 30 * 1000);
};

export const installSupportGrantInterceptor = () => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  axios.interceptors.request.use((config) => {
    const grant = getSupportGrant();
    if (!grant) return config;

    if (isUserDataRequest(config)) {
      if (userDataInFlight) {
        return Promise.reject({
          __dedupedSupportGrantUser: true,
          config,
          message: "Duplicate /v1/user request suppressed",
        });
      }
      userDataInFlight = true;
    }

    config.headers = config.headers || {};
    delete config.headers.Authorization;
    delete config.headers.authorization;
    config.headers["x-support-grant"] = grant;
    config.headers["Cache-Control"] = "no-cache";
    config.headers["Pragma"] = "no-cache";
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      if (isUserDataRequest(response?.config)) {
        userDataInFlight = false;
      }
      return response;
    },
    (error) => {
      if (error?.__dedupedSupportGrantUser) {
        return Promise.reject(error);
      }
      if (isUserDataRequest(error?.config)) {
        userDataInFlight = false;
      }
      if (error?.response?.status === 401 && isInSupportMode()) {
        goToSupportExpired();
      }
      return Promise.reject(error);
    },
  );

  scheduleExpiryWatcher();
};
